/**
 * PopulationMaster — entity-scoped CRUD for adt_populations.
 *
 * Mounted at: OrganisationView -> Masters tab -> OrgMasters -> Audit group ->
 *   "Populations" leaf. Populations are an audit master; this screen shares the
 *   audit RBAC roles via the ADT_PROGRAMS activity (also gates the backend
 *   write endpoints). Legacy ?tab=population redirects to
 *   ?tab=masters&section=audit-populations.
 * API:  GET/POST/PUT/DELETE  /api/v1/adt/populations  (existing entity-scoped endpoints).
 * Skills: table-design1 (netbody-card / table-list-* pattern), no Tailwind layout utilities.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Edit2, Save, Check, CircleMinus, CircleCheck, Lock } from 'lucide-react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import Pagination from '../common/Pagination';
import Skeleton from '../common/Skeleton';
import NoDataFound from '../common/NoDataFound';
import Toast, { ToastMessage } from '../common/Toast';
import {
  Population,
  PopulationCreatePayload,
  populationService,
} from '../../services/auditService';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea } from '../../validation/ValidatedFields';
import { isUniqueLocal } from '../../validation/uniqueness';
import { usePermission, NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';
import { confirmWarning, confirmActive } from '../common/confirm';
import ApprovalDot, { WorkflowState } from '../common/ApprovalDot';
import WorkflowModal from '../common/WorkflowModal';
import StatusSwitch from '../common/StatusSwitch';

const WF_KIND = 'adt_population';

// Populations are an audit master — they share the audit RBAC roles via the
// ADT_PROGRAMS activity, the same activity that already gates the backend
// populations write endpoints.
const RBAC_ACTIVITY = 'ADT_PROGRAMS';

const PopulationMaster: React.FC = () => {
  const vh = useFieldValidation();
  const viewPerm = usePermission(RBAC_ACTIVITY, 'view');
  const modifyPerm = usePermission(RBAC_ACTIVITY, 'view+modify');
  const canView = viewPerm.allowed;
  const canModify = modifyPerm.allowed;
  const [rows, setRows] = useState<Population[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  // Toolbar state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive'>('active');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  // View-first edit: existing records open read-only; "Edit" enables the form.
  const [isEditing, setIsEditing] = useState(false);
  const [selectedRow, setSelectedRow] = useState<Population | null>(null);
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);

  // Workflow timeline modal (shared, kind-based).
  const [workflowRow, setWorkflowRow] = useState<Population | null>(null);

  // Approved state — determines if form is locked
  const isApproved = selectedRow?.workflow_state === 'approved';
  // Fields are read-only while viewing an existing record (before "Edit").
  const fieldsDisabled = isEditMode && (!isEditing || !canModify || isApproved);

  // ── Data fetching ─────────────────────────────────────────────────────────

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const data = await populationService.listPopulations();
      setRows(data);
    } catch (err: any) {
      setToast({ type: 'error', message: err?.message ?? 'Failed to load populations' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Reset to page 1 whenever filters change
  useEffect(() => { setPage(1); }, [searchTerm, statusFilter, pageSize]);

  // ── Filtering + pagination ────────────────────────────────────────────────

  const filteredRows = rows.filter(r => {
    if (statusFilter === 'active' && r.is_active !== 1) return false;
    if (statusFilter === 'inactive' && r.is_active === 1) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      const nameMatch = r.population_name.toLowerCase().includes(q);
      const descMatch = (r.population_description ?? '').toLowerCase().includes(q);
      if (!nameMatch && !descMatch) return false;
    }
    return true;
  });

  const totalRecords = filteredRows.length;
  const paginatedRows = filteredRows.slice((page - 1) * pageSize, page * pageSize);

  // ── Modal helpers ─────────────────────────────────────────────────────────

  const openAdd = () => {
    setIsEditMode(false);
    setIsEditing(true); // create is always editable
    setSelectedRow(null);
    setFormName('');
    setFormDesc('');
    vh.reset();
    setShowModal(true);
  };

  // Open an existing record read-only; the footer "Edit" enables the form.
  const openView = (row: Population) => {
    setIsEditMode(true);
    setIsEditing(false);
    setSelectedRow(row);
    setFormName(row.population_name);
    setFormDesc(row.population_description ?? '');
    vh.reset();
    setShowModal(true);
  };

  const closeModal = () => { vh.reset(); setShowModal(false); };

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (fieldsDisabled) return;
    const engineOk = vh.validateAll([
      { name: 'name', code: 109, value: formName, required: true },
      { name: 'description', code: 110, value: formDesc },
    ]);
    if (!engineOk) return;
    if (formName.trim() && !isUniqueLocal(906, formName.trim(), rows, selectedRow?.id)) {
      vh.setError('name', 'A population with this name already exists.');
      return;
    }
    setSaving(true);
    try {
      if (isEditMode && selectedRow) {
        await populationService.updatePopulation(selectedRow.id, {
          population_name: formName.trim(),
          population_description: formDesc.trim() || null,
        });
        setToast({ type: 'success', message: 'Population updated.' });
      } else {
        const payload: PopulationCreatePayload = {
          population_name: formName.trim(),
          population_description: formDesc.trim() || null,
        };
        await populationService.createPopulation(payload);
        setToast({ type: 'success', message: 'Population created.' });
      }
      closeModal();
      refresh();
    } catch (err: any) {
      setToast({ type: 'error', message: err?.message ?? 'Failed to save population.' });
    } finally {
      setSaving(false);
    }
  };

  // ── Activate / Inactivate ───────────────────────────────────────────────────

  const handleToggleActive = async () => {
    if (!selectedRow || !canModify) return;
    const isActive = (selectedRow.is_active ?? 1) === 1;
    const next = isActive ? 0 : 1;
    const confirmFn = isActive ? confirmWarning : confirmActive;
    const ok = await confirmFn({
      title: next === 1 ? 'Reactivate this population?' : 'Inactivate this population?',
      text: next === 1 ? 'It will appear in active lists again.' : 'It will be hidden from active lists.',
      confirmText: next === 1 ? 'Yes, Reactivate' : 'Yes, Inactivate',
    });
    if (!ok) return;
    setToggling(true);
    try {
      await populationService.updatePopulation(selectedRow.id, { is_active: next });
      setToast({ type: next === 1 ? 'success' : 'warning', message: next === 1 ? 'Reactivated.' : 'Inactivated.' });
      closeModal();
      refresh();
    } catch (err: any) {
      setToast({ type: 'error', message: err?.message ?? 'Failed to update status.' });
    } finally {
      setToggling(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (!viewPerm.loading && !canView) {
    return (
      <div className="netbody-card flex flex-col h-full min-h-[300px] items-center justify-center">
        <NoDataFound title="Not authorised" message="You do not have permission to view Populations." />
      </div>
    );
  }

  return (
    <div className="netbody-card flex flex-col h-full">

      {/* Toolbar — Status switch LEFT · search + Add RIGHT */}
      <div className="netbody-header">
        <StatusSwitch
          value={statusFilter === 'inactive' ? 'INACTIVE' : 'ACTIVE'}
          onChange={next => setStatusFilter(next === 'INACTIVE' ? 'inactive' : 'active')}
          noun="populations"
          testId="BTN_population_status"
        />
        <div className="flex items-center gap-2 ml-auto">
          <FormControl.Input
            type="text"
            placeholder="Search populations…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            leadingIcon={<Search size={14} />}
            className="filter-search"
            data-testid="INP_org_populations_search"
          />
          {canModify && (
            <Button
              variant="fill"
              icon={Plus}
              onClick={openAdd}
              data-testid="BTN_org_population_add"
            >
              Create
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="netbody-content">
        <div className="scroll-container flex-1 overflow-hidden">
          <div className="table-list" data-testid="TBL_org_populations">
            <div className="table-list-scroll">
              <table className="table-list-table">
                <thead>
                  <tr>
                    <th className="table-list-th table-list-cell--dot" />
                    <th className="table-list-th table-list-cell--sno">S.No</th>
                    <th className="table-list-th table-list-cell--code">Code</th>
                    <th className="table-list-th">Population Name</th>
                    <th className="table-list-th table-list-cell--truncate">Description</th>
                  </tr>
                </thead>
                <tbody aria-busy={loading} aria-live="polite">
                  {loading && (
                    <Skeleton.TableRows
                      cols={[
                        '3xs',
                        '2xs',
                        '2xs',
                        'md',
                        'md',
                      ]}
                    />
                  )}
                  {!loading && filteredRows.length === 0 && (
                    <tr>
                      <td colSpan={5} className="table-list-cell">
                        <NoDataFound
                          title="No populations found"
                          message='Click "Add Population" to create your first population label.'
                        />
                      </td>
                    </tr>
                  )}
                  {!loading && paginatedRows.map((row, idx) => (
                    <tr
                      key={row.id}
                      className="table-list-row"
                      onClick={() => openView(row)}
                    >
                      <td className="table-list-cell table-list-cell--dot" onClick={e => { e.stopPropagation(); setWorkflowRow(row); }} title="Workflow — view timeline & act">
                        <ApprovalDot
                          state={(row.workflow_state || 'draft') as WorkflowState}
                          onClick={() => setWorkflowRow(row)}
                        />
                      </td>
                      <td className="table-list-cell table-list-cell--sno">
                        {(page - 1) * pageSize + idx + 1}
                      </td>
                      <td className="table-list-cell table-list-cell--code">
                        <span className="cell-code">{row.code ?? '—'}</span>
                      </td>
                      <td className="table-list-cell">
                        <span className="table-list-cell-title cell-name-lock">
                          {row.workflow_state === 'approved' && (
                            <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                          )}
                          {row.population_name}
                        </span>
                      </td>
                      <td className="table-list-cell table-list-cell--truncate" title={row.population_description ?? ''}>
                        {row.population_description ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              pageSize={pageSize}
              totalRecords={totalRecords}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              recordLabel="populations"
            />
          </div>
        </div>
      </div>

      {/* Create / Edit modal */}
      <Modal isOpen={showModal} onClose={closeModal} size="md" data-testid="MOD_org_population_form">
        <Modal.Header>
          {isEditMode ? (isApproved ? 'Viewing (locked) Population' : (isEditing ? 'Edit Population' : 'View Population')) : 'Create Population'}
        </Modal.Header>
        <Modal.Body>
          {isApproved && (
              <div className="approved-banner">
                <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
              </div>
            )}
          <form id="population-form" onSubmit={handleSubmit} className="grid grid-cols-1 gap-y-4">
            {/* System code — read-only, prefilled with the same code the list shows. */}
            {isEditMode && (
              <FormControl.Input
                label="Code"
                value={(selectedRow as any)?.code || '—'}
                disabled
                data-testid="INP_population_code"
              />
            )}
            <ValidatedInput
              vh={vh}
              name="name"
              code={109}
              label="Population Name"
              required
              type="text"
              placeholder="e.g. Vendors Q2"
              value={formName}
              onChange={e => setFormName(e.target.value)}
              data-testid="INP_org_population_name"
              disabled={fieldsDisabled}
            />
            <ValidatedTextarea
              vh={vh}
              name="description"
              code={110}
              label="Description"
              placeholder="Optional description…"
              value={formDesc}
              onChange={e => setFormDesc(e.target.value)}
              rows={3}
              data-testid="INP_org_population_desc"
              disabled={fieldsDisabled}
            />
          </form>
        </Modal.Body>
        <Modal.Footer className={isEditMode ? 'app-modal-footer--split' : ''}>
          {/* Active/Inactive toggle — shown alongside Edit (view) and Update (edit), but not if approved */}
          {isEditMode && selectedRow && !isApproved && (
            <Button
              variant={(selectedRow.is_active ?? 1) === 1 ? 'inactive-outline' : 'active-outline'}
              icon={(selectedRow.is_active ?? 1) === 1 ? CircleMinus : CircleCheck}
              onClick={handleToggleActive}
              disabled={saving || toggling || !canModify}
              title={!canModify ? NOT_AUTHORISED_MSG : undefined}
              data-testid="BTN_org_population_toggle_active"
            >
              {toggling ? '...' : (selectedRow.is_active ?? 1) === 1 ? 'Inactive' : 'Active'}
            </Button>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            {/* Create → Submit. */}
            {!isEditMode && (
              <Button
                type="submit"
                form="population-form"
                variant="fill"
                icon={Save}
                disabled={saving || !canModify || !formName.trim()}
                title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                data-testid="BTN_org_population_submit"
              >
                Submit
              </Button>
            )}
            {/* View mode → "Edit" enables the form (but not if approved). */}
            {isEditMode && !isEditing && !isApproved && (
              <Button
                variant="fill"
                icon={Edit2}
                onClick={() => {
                  if (!canModify) { setToast({ type: 'error', message: NOT_AUTHORISED_MSG }); return; }
                  setIsEditing(true);
                }}
                disabled={!canModify}
                title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                data-testid="BTN_org_population_edit"
              >
                Edit
              </Button>
            )}
            {/* Edit mode → "Update" saves (but not if approved). */}
            {isEditMode && isEditing && !isApproved && (
              <Button
                type="submit"
                form="population-form"
                variant="update"
                icon={Check}
                disabled={saving || toggling || !canModify || !formName.trim()}
                title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                data-testid="BTN_org_population_submit"
              >
                {saving ? 'Updating...' : 'Update'}
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>

      <WorkflowModal
        isOpen={!!workflowRow}
        onClose={() => { setWorkflowRow(null); refresh(); }}
        kind={WF_KIND}
        id={workflowRow?.id ?? null}
        onChanged={refresh}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default PopulationMaster;
